import type { JSX } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { useI18n } from '../../i18n';
import { Button } from '../ui/button';

/**
 * AU-7 (mockup studio-ai-chat-preview-v2.html askUndo()): the undo met the GM's own edit of the same thing after the
 * answer — nothing was undone; «Restore as before» (the edit is lost, by consent), «Keep my edits, undo the rest»
 * (the edited things stay), or «Cancel».
 */
export interface AiUndoConflictDialogProps {
  readonly open: boolean;
  /** The edited things, in words. */
  readonly items: readonly string[];
  /** Is there anything besides the edited things that «Keep my edits» would still undo? */
  readonly hasRest: boolean;
  readonly onRestore: () => void;
  readonly onKeepMine: () => void;
  readonly onCancel: () => void;
}

export function AiUndoConflictDialog({ open, items, hasRest, onRestore, onKeepMine, onCancel }: AiUndoConflictDialogProps): JSX.Element {
  const { t } = useI18n();
  return (
    <Dialog open={open} onOpenChange={(o): void => { if (!o) onCancel(); }}>
      <DialogContent className="max-w-[420px]">
        <DialogHeader>
          <DialogTitle>{t('aiUndo.conflictTitle')}</DialogTitle>
          <DialogDescription>{t(items.length > 1 ? 'aiUndo.conflictBodyMany' : 'aiUndo.conflictBodyOne')}</DialogDescription>
        </DialogHeader>
        {items.length > 0 ? (
          <ul className="list-disc pl-5 text-ui text-fg">
            {items.map((item, i) => <li key={i}>{item}</li>)}
          </ul>
        ) : null}
        <div className="flex flex-wrap items-center gap-2">
          <Button type="button" size="sm" onClick={onRestore}>
            {t('aiUndo.restore')}
          </Button>
          <Button type="button" size="sm" variant="outline" onClick={onKeepMine}>
            {t(hasRest ? 'aiUndo.keepRest' : 'aiUndo.keepOnly')}
          </Button>
          <Button type="button" size="sm" variant="text" onClick={onCancel}>
            {t('aiUndo.cancel')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
